import { useFlag } from '@reflag/react-sdk';
import { skipToken, useSuspenseQueries } from '@tanstack/react-query';
import * as React from 'react';

import * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { type ContentReferenceContext, isGitBookStaff } from '@gitbook/app-database';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import { useHivePreloadBranch } from '@gitbook/hive-react';
import { ModalAnimateLifecycle } from '@gitbook/react-uikit';
import {
    DialogRoot,
    LoadingPanel,
    useDialogTriggerState,
    useScreenType,
} from '@gitbook/spine-react';
import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import {
    APIErrorBoundary,
    ChangeRequestOverview,
    type ChangeRequestOverviewProps,
    ContentReferenceContextProvider,
    GitSyncProgressModal,
    InsertFilesDialog,
    type NavigateToCommentsFn,
    SpaceContext,
    useGitSyncCurrentStatus,
} from '../../../Components';
import { ChangeRequestMergeProvider } from '../../../Components/ChangeRequest/ChangeRequestMergeProvider';
import { NavigationRedirect, useNavigation } from '../../../navigation';
import { apiQuery, useCurrentUser, useLockSidebar } from '../../../state';
import { IntegrationsModal } from '../../Integrations';
import { GitSyncInstallationDialog } from '../GitSyncInstallationDialog';
import { HeaderSpace } from '../Header';
import { SpacePreview } from '../Preview';
import { SpaceScreenSkeleton } from '../Skeleton';
import { useAutoSaveToast } from '../utils';
import { ChangeRequestNotAuthorized } from './ChangeRequestNotAuthorized';
import { ChangeRequestNotFound } from './ChangeRequestNotFound';
import { SpaceContent, type SpaceContentProps } from './SpaceContent';
import { SpaceContentNotFound } from './SpaceContentNotFound';
import { SpaceDeleted } from './SpaceDeleted';
import { SpaceDuplicatingAlert } from './SpaceDuplicatingAlert';
import { SpaceGitSyncErrorDialog } from './SpaceGitSyncErrorDialog';
import { SpaceHeadTitle } from './SpaceHeadTitle';
import { SpaceMergeRulesDialog } from './SpaceMergeRulesDialog';
import { SpaceNotAuthorized } from './SpaceNotAuthorized';
import { SpaceNotFound } from './SpaceNotFound';
import { SpacePublishDialog } from './SpacePublishDialog';
import { SpaceScreenSidePanel, shouldDisplaySidePanel } from './SpaceScreenSidePanel';
import { SpaceShareDialog } from './SpaceShareDialog';
import { WithSpaceHiveContext } from './WithSpaceHiveContext';

/**
 * Main component to render a space screen.
 */
export function SpaceScreen(props: { screen: NavigationScreenSpaceState }): React.ReactElement {
    const { screen } = props;
    const { navigate } = useNavigation();

    // Lock the sidebar when a side panel is open, we do this before loading to avoid layout shifts.
    const crScreenFeature = useFlag('CHANGE_REQUESTS_SCREEN');
    const withSidePanel = shouldDisplaySidePanel(screen);
    useLockSidebar(withSidePanel, () => {
        if (withSidePanel) {
            navigate({ screen: { sideSheet: undefined } });
        }
    });

    // Compute the permission level the user has with the space
    const currentUser = useCurrentUser();
    const isStaff = isGitBookStaff(currentUser);

    // If we are on a overview sheet, redirect to the change request overview.
    if (
        screen.sideSheet?.type === 'overview' &&
        screen.changeRequest &&
        crScreenFeature.isEnabled
    ) {
        return (
            <NavigationRedirect
                to={{ screen: { mode: undefined, sideSheet: undefined } }}
                replace
            />
        );
    }

    return (
        <React.Suspense fallback={<SpaceScreenSkeleton withSidePanel={withSidePanel} />}>
            <APIErrorBoundary
                fallback={{
                    403: !isStaff ? (
                        screen.changeRequest ? (
                            <ChangeRequestNotAuthorized />
                        ) : (
                            <SpaceNotAuthorized screen={screen} />
                        )
                    ) : undefined,
                    404: props.screen.changeRequest ? <ChangeRequestNotFound /> : <SpaceNotFound />,
                }}
            >
                <SpaceScreenFetcher {...props} isStaff={isStaff} />
            </APIErrorBoundary>
        </React.Suspense>
    );
}

function SpaceScreenFetcher(props: {
    screen: NavigationScreenSpaceState;
    isStaff: boolean;
}): React.ReactElement {
    const { screen, isStaff } = props;

    // Preload the main content, before we suspend the rendering to fetch the space.
    useHivePreloadBranch(
        screen.revision || screen.changeRequest
            ? null
            : {
                  contentId: screen.space,
                  branchId: undefined,
              }
    );

    const [{ data: space }, { data: changeRequest }] = useSuspenseQueries({
        queries: [
            apiQuery('getSpaceById', {
                spaceId: screen.space,
            }),
            apiQuery(
                'getChangeRequestById',
                screen.changeRequest
                    ? {
                          spaceId: screen.space,
                          changeRequestId: screen.changeRequest.toString(),
                      }
                    : skipToken
            ),
        ],
    });

    assertIsDefined(space);

    if (space.organization !== screen.organization) {
        // Include the organization in the navigate state if it's not present.
        return (
            <NavigationRedirect replace to={{ current: { organization: space.organization } }} />
        );
    }

    if (space.deletedAt) {
        return <SpaceDeleted space={space} />;
    }

    if (!space.permissions.access && !isStaff) {
        return <SpaceNotAuthorized space={space} screen={screen} />;
    }

    if (typeof screen.changeRequest === 'string' && changeRequest) {
        // Redirect to the canonical url for a change-request, where the identifier in the url is the number
        return (
            <NavigationRedirect
                replace
                to={{
                    current: {
                        changeRequest: changeRequest.number,
                    },
                }}
            />
        );
    }

    return (
        <SpaceScreenWithSpace
            space={space}
            changeRequest={changeRequest}
            screen={screen}
            isStaffAccess={!space.permissions.access && isStaff}
        />
    );
}

/**
 * Screen with an actual existing space.
 */
function SpaceScreenWithSpace(props: {
    screen: NavigationScreenSpaceState;
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
    isStaffAccess: boolean;
}): React.ReactElement {
    const { screen, space, changeRequest, isStaffAccess } = props;

    const { navigate } = useNavigation();
    const { isMobile } = useScreenType();

    useAutoSaveToast();

    const gitSyncStatus = useGitSyncCurrentStatus(space);
    const gitSyncFailed =
        gitSyncStatus?.state === 'failure' || gitSyncStatus?.state === 'timed-out';

    const [showGitSyncError, setShowGitSyncError] = React.useState(false);
    const gitSyncErrorDialog = useDialogTriggerState();

    /**
     * Context for events in this space.
     * Passed down the tree and memoized for performances (it should not change when editing).
     */
    const analyticsContext = React.useMemo<analytics.AnalyticsContextSpaceContent>(
        () =>
            analytics.createEditEvent(
                {
                    id: space.id,
                    parent: space.parent,
                    organization: space.organization,
                },
                changeRequest?.id,
                {}
            ),
        [space.id, space.parent, space.organization, changeRequest?.id]
    );

    React.useEffect(() => {
        setShowGitSyncError(
            Boolean(space.gitSync?.installationProvider && gitSyncFailed && space.permissions.edit)
        );
    }, [gitSyncFailed, space.gitSync?.installationProvider, space.permissions.edit]);

    /**
     * A context for any content reference resolution. Note that it may be overriden by more
     * specific context further down the component tree.
     */
    const contentReferenceContext: ContentReferenceContext = {
        organization: space.organization,
        space: space.id,
        ...(changeRequest ? { changeRequest: changeRequest?.id } : {}),
    };

    const { installationProvider } = space.gitSync || {};
    const showGitSyncProgressModal = Boolean(
        // We don't want to stack the modal on top of the error modal
        !showGitSyncError &&
            screen.sideSheet?.type === 'gitsync' &&
            screen.sideSheet?.tab === 'progress'
    );

    const readOnly =
        !!screen.revision ||
        (!!changeRequest &&
            [api.ChangeRequestStatus.Merged, api.ChangeRequestStatus.Archived].includes(
                changeRequest.status
            )) ||
        // Staff admins can't edit content to avoid any side-effects
        isStaffAccess ||
        // We don't support editing on mobile
        isMobile ||
        // Locked spaces can't be edited
        (!changeRequest && space.editMode === api.SpaceEditMode.Locked) ||
        // Diff views are read-only
        screen.mode === 'diff' ||
        // The user should have the right permissions
        !space.permissions.edit ||
        false;

    const context = React.useMemo(
        () => ({ space, changeRequest, analyticsContext, readOnly }),
        [space, changeRequest, analyticsContext, readOnly]
    );

    return (
        <SpaceContext.Provider value={context}>
            <ContentReferenceContextProvider value={contentReferenceContext}>
                <DialogRoot
                    {...gitSyncErrorDialog.rootProps}
                    isOpen={showGitSyncError}
                    onOpenChange={(isOpen) => setShowGitSyncError(isOpen)}
                >
                    <SpaceGitSyncErrorDialog space={space} gitSyncStatus={gitSyncStatus} />
                </DialogRoot>

                <ModalAnimateLifecycle>
                    {showGitSyncProgressModal && installationProvider ? (
                        <GitSyncProgressModal
                            space={space}
                            installationProvider={installationProvider}
                            gitSyncStatus={gitSyncStatus}
                            onClose={() =>
                                navigate({
                                    current: {
                                        sideSheet: null,
                                    },
                                })
                            }
                        />
                    ) : null}
                </ModalAnimateLifecycle>

                <React.Suspense fallback={null}>
                    <IntegrationsModal screen={screen} />
                </React.Suspense>

                <SpaceShareDialog space={space} />
                <SpacePublishDialog space={space} />
                <SpaceMergeRulesDialog space={space} />

                <GitSyncInstallationDialog space={space} />

                <SpaceScreenSidePanel
                    space={space}
                    analyticsContext={analyticsContext}
                    screen={screen}
                    changeRequest={changeRequest}
                    readOnly={readOnly}
                >
                    <WithSpaceHiveContext
                        space={space}
                        changeRequest={changeRequest}
                        readOnly={readOnly}
                        screen={screen}
                        notFound={<SpaceContentNotFound spaceId={space.id} />}
                    >
                        <ChangeRequestMergeProvider
                            value={{ space, changeRequest, analyticsContext }}
                        >
                            {readOnly ? null : (
                                <InsertFilesDialog space={space} changeRequest={changeRequest} />
                            )}

                            <HeaderSpace
                                space={space}
                                changeRequest={changeRequest}
                                screen={screen}
                                analyticsContext={analyticsContext}
                            />

                            <SpaceDuplicatingAlert>
                                <React.Suspense fallback={<LoadingPanel size="large" />}>
                                    {(() => {
                                        switch (screen.mode) {
                                            case 'edit':
                                            case 'diff':
                                                return (
                                                    <SpaceContent
                                                        space={space}
                                                        screen={screen}
                                                        changeRequest={changeRequest}
                                                        analyticsContext={analyticsContext}
                                                        readOnly={readOnly}
                                                    />
                                                );
                                            case 'preview':
                                                return (
                                                    <SpacePreview
                                                        space={space}
                                                        changeRequest={changeRequest}
                                                        screen={screen}
                                                        analyticsContext={analyticsContext}
                                                    />
                                                );
                                            case undefined: {
                                                return (
                                                    <DefaultSpaceContent
                                                        space={space}
                                                        screen={screen}
                                                        changeRequest={changeRequest}
                                                        analyticsContext={analyticsContext}
                                                        readOnly={readOnly}
                                                    />
                                                );
                                            }
                                            default:
                                                assertNever(screen.mode);
                                        }
                                    })()}
                                </React.Suspense>
                            </SpaceDuplicatingAlert>
                        </ChangeRequestMergeProvider>
                    </WithSpaceHiveContext>
                </SpaceScreenSidePanel>
            </ContentReferenceContextProvider>
        </SpaceContext.Provider>
    );
}

function DefaultSpaceContent(props: SpaceContentProps) {
    const { changeRequest, analyticsContext, space, screen } = props;
    const crScreenFeature = useFlag('CHANGE_REQUESTS_SCREEN');
    if (changeRequest && crScreenFeature.isEnabled) {
        return (
            <SpaceContentChangeRequestOverview
                analyticsContext={analyticsContext}
                changeRequest={changeRequest}
                space={space}
                currentPageId={screen.page ?? null}
                currentSideSheet={
                    screen.sideSheet?.type === 'comments' ? screen.sideSheet : undefined
                }
            />
        );
    }
    return <SpaceContent {...props} />;
}

function SpaceContentChangeRequestOverview(
    props: Omit<ChangeRequestOverviewProps, 'navigateToComments'>
) {
    const { navigate } = useNavigation();
    const navigateToComments: NavigateToCommentsFn = (input) => {
        navigate({
            current: {
                page: input.pageId,
                sideSheet: input.sideSheet,
            },
        });
    };

    return (
        <>
            <SpaceHeadTitle
                title="Overview"
                space={props.space}
                changeRequest={props.changeRequest}
            />
            <ChangeRequestOverview {...props} navigateToComments={navigateToComments} />
        </>
    );
}
